<template>
    <div    class="">
        <div>
            <h5 class="text-left text-sm font-semibold text-gray-800 mt-4 mb-3">Notification Schedule</h5>
            <div    class="grid grid-cols-1 mb-2">
                <div    class="mt-1 sm:mt-0 ">
                    <span class="relative z-0 flex shadow-sm rounded-md w-full lg:w-auto">
                        <button     type="button"
                                    @click="updateTimeChoice('24/7')"
                                    :class="timeChoice === '24/7' ? 'bg-blue-200' : 'bg-white'"
                                    class="relative flex-1 inline-flex justify-center items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                            24/7
                        </button>
                        <button     type="button"
                                    @click="updateTimeChoice('Work Week')"
                                    :class="timeChoice === 'Work Week' ? 'bg-blue-200' : 'bg-white'"
                                    class="-ml-px relative flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                            Work Week
                        </button>
                        <button     type="button"
                                    @click="updateTimeChoice('Custom')"
                                    :class="timeChoice === 'Custom' ? 'bg-blue-200' : 'bg-white'"
                                    class="-ml-px relative flex-1 inline-flex justify-center items-center px-4 py-2 rounded-r-md border border-gray-300 text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                            Custom
                        </button>
                    </span>
                </div>
                <div    class="mt-3">
                    <!-- 24/7 Notes -->
                    <div    v-if="timeChoice === '24/7'"
                            class="rounded-md bg-blue-50 px-3 py-3 text-sm text-blue-700">
                        You'll receive alarm notifications whenever they occur, day or night.
                    </div>

                    <!-- Work Week Notes -->
                    <div    v-if="timeChoice === 'Work Week'"
                            class="rounded-md bg-blue-50 px-3 py-3 text-sm text-blue-700">
                        You'll only receive alarms Monday through Friday for the time range you input below.
                    </div>

                    <!-- Custom Notes -->
                    <div    v-if="timeChoice === 'Custom'"
                            class="rounded-md bg-blue-50 px-3 py-3 text-sm text-blue-700">
                        Create a custom notification schedule for each day.
                    </div>
                </div>
            </div>
            <!-- Setup for Custom -->
            <div    v-if="timeChoice === 'Custom' && timesValid">
                <div    v-for="(dayTimes, day) in times"
                        class="mt-5 border-t border-gray-200 pt-5">
                    <!-- Day header row: name + 24h toggle -->
                    <div class="flex items-center justify-between mb-3">
                        <span class="font-medium text-base text-gray-800">{{ day }}</span>
                        <button-checkbox    v-model:value="isAllDay[day]"
                                            @update:value="customAllDay(day)"
                        >
                            24 Hours
                        </button-checkbox>
                    </div>
                    <!-- Time range rows -->
                    <div v-if="!isAllDay[day]">
                        <div v-for="(time, index) in dayTimes" :key="index">
                            <div
                                v-if="times[day].length > 0"
                                class="flex items-center gap-2 mb-2">
                                <div class="flex-1">
                                    <date-time-picker
                                        class="w-full"
                                        v-model="times[day][index]['start']"
                                        @update:modelValue="formatTimesForServer"
                                        :side-by-side="true"
                                        format="h a"
                                        vertical-position="top"
                                        :id="`${day}_${index}_start`"
                                    ></date-time-picker>
                                </div>
                                <span class="text-sm text-gray-500 shrink-0">until</span>
                                <div class="flex-1">
                                    <date-time-picker
                                        v-model="times[day][index].end"
                                        @update:modelValue="formatTimesForServer"
                                        format="h a"
                                        vertical-position="top"
                                        :id="`${day}_${index}_end`"
                                    ></date-time-picker>
                                </div>
                                <span   class="cursor-pointer lnr icon-trash text-gray-400 hover:text-red-500 shrink-0 text-xl p-1"
                                        @click="removeTime(day, index)"
                                ></span>
                            </div>
                        </div>
                        <button-blue-secondary  @click="addTime(day)" class="mt-1 w-full">
                            + Add Time
                        </button-blue-secondary>
                    </div>
                </div>
            </div>

            <!-- Setup For Work Week -->
            <div    v-if="timeChoice === 'Work Week' && timesValid">
                <div class="flex items-center gap-2 mt-5">
                    <div class="flex-1">
                        <date-time-picker
                            class="w-full"
                            v-model="workWeekStart"
                            @update:modelValue="updateWorkWeek"
                            :side-by-side="true"
                            format="h a"
                            vertical-position="top"
                            :id="`work_week_start`"
                        ></date-time-picker>
                    </div>
                    <span class="text-sm text-gray-500 shrink-0">until</span>
                    <div class="flex-1">
                        <date-time-picker
                            class="w-full"
                            v-model="workWeekEnd"
                            @update:modelValue="updateWorkWeek"
                            :side-by-side="true"
                            format="h a"
                            vertical-position="top"
                            :id="`work_week_end`"
                        ></date-time-picker>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import DateTimePicker from "@/components/Reports/VueDatePicker";
import ButtonCheckbox from "@/components/elements/buttons/ButtonCheckbox";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";
import _ from "lodash";

export default {
    name: "UserNotificationTimesEdit",
    mixins: [iconNamesMixin],
    components: {ButtonBlueSecondary, ButtonCheckbox, DateTimePicker},
    model: {
        prop: 'times',
        event: 'update:times'
    },
    props: {
        times: Object,
        serverTimes: Object,
        timeChoice: {
            type: String,
            default: '24/7',
        },
    },
    data() {
        return {
            defaultNotificationTimeOptions: {
                '24/7' : {
                    'Sunday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Monday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Tuesday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Wednesday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Thursday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Friday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Saturday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                },
                'Work Week' : {},
                'Custom' : {
                    'Sunday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Monday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Tuesday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Wednesday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Thursday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Friday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                    'Saturday': [
                        {
                            start: 0,
                            end: 0,
                        },
                    ],
                },
            },
            isAllDay: {
                'Sunday': false,
                'Monday': false,
                'Tuesday': false,
                'Wednesday': false,
                'Thursday': false,
                'Friday': false,
                'Saturday': false,
            },
            timesValid: false,
            workWeekStart: this.makeDateFromHour(8),
            workWeekEnd: this.makeDateFromHour(17),
        }
    },
    created() {
    },
    mounted() {
        if (!_.isNil(this.times)) {
            this.defaultNotificationTimeOptions[this.timeChoice] = this.times
        }

        this.setTimes()

        if (this.timeChoice === 'Work Week') {
            this.workWeekStart = this.defaultNotificationTimeOptions['Work Week']['Monday'][0]['start']
            this.workWeekEnd = this.defaultNotificationTimeOptions['Work Week']['Monday'][0]['end']
        }
        this.formatTimesForServer()
        this.updateAllDayTimes()
    },
    methods: {
        updateServerTimes(times) {
            this.$emit('update:serverTimes', times)
        },
        updateTimes(times) {
            this.$emit('update:times', times)
            this.$forceUpdate()
        },
        updateTimeChoice(choice) {
            this.$emit('update:timeChoice', choice)
            if (choice === 'Work Week') {
                this.updateWorkWeek()
            }
            else if (choice === 'Custom') {
                this.updateCustom()
            }
            else {
                this.formatTimesForServer()
            }
        },
        updateWorkWeek() {
            let times = {}
            for (let index = 1; index < 6; index++) {
                let days = Object.keys(this.defaultNotificationTimeOptions['24/7'])
                times[days[index]] = [{
                    start: this.workWeekStart,
                    end: this.workWeekEnd,
                }]
            }
            this.updateTimes(times)
        },
        updateCustom() {
            let times = _.clone(this.defaultNotificationTimeOptions['Custom'])
            this.updateTimes(times)
        },
        makeDateFromHour(hour) {
            return new Date(0, 0, 0, hour)
        },
        getHourFromDateOrHour(dateOrHour) {
            if (_.isDate(dateOrHour)) {
                return dateOrHour.getHours()
            }
            return dateOrHour
        },
        customAllDay(day) {
            let times = this.times

            if (this.isAllDay[day]) {
                times[day] = [
                    {
                        start: this.makeDateFromHour(0),
                        end: this.makeDateFromHour(0),
                    },
                ];
            }
            this.updateTimes(times)
            this.formatTimesForServer()
        },
        addTime(day) {
            let times = this.times
            let start = 0
            if (times[day].length > 0) {
                let last = times[day].length - 1
                start = times[day][last].end.getHours()
            }

            times[day].push( {
                start: this.makeDateFromHour(start),
                end: this.makeDateFromHour(0),
            })

            this.updateTimes(times)
            this.formatTimesForServer()
        },
        removeTime(day, index) {
            let times = this.times
            day = times[day]
            if (index < day.length) {
                day.splice(index, 1)
            }

            this.updateTimes(times)
            this.formatTimesForServer()
        },
        setTimes() {
            this.timesValid = false
            if (!_.isNil(this.timeChoice)) {
                this.updateTimeChoice(this.timeChoice)
            }

            let times = this.times
                this.notificationTimesOptionsValid = true
                this.timesValid = true
                this.formatTimesForServer()

            if (this.timeChoice !== 'Work Week') {
                times = this.defaultNotificationTimeOptions[this.timeChoice]
            }

            Object.keys(times).forEach(day => {
                let dayTimes = times[day]
                dayTimes.forEach((interval, index) => {
                    interval.start = _.isNumber(interval.start) ? this.makeDateFromHour(interval.start) : interval.start
                    interval.end =  _.isNumber(interval.end) ? this.makeDateFromHour(interval.end) : interval.end
                    times[day][index] = interval
                })
            })
            this.updateTimes(times)
            this.formatTimesForServer()
            this.notificationTimesOptionsValid = true
            this.timesValid = true
        },
        formatTimesForServer() {
            if (_.isNil(this.times)) {
                return
            }
            let times = JSON.parse(JSON.stringify(this.times))
            if (_.isNil(times)) {
                times = {}
            }
            Object.keys(times).forEach(day => {
                let tmp = times[day]
                tmp.forEach((interval, index) => {
                    times[day][index].start = _.isNumber(interval.start) ? interval.start : (new Date(interval.start)).getHours()
                    times[day][index].end = _.isNumber(interval.end) ? interval.end : (new Date(interval.end)).getHours()
                })
            })
            this.updateServerTimes(times)
        },
        updateAllDayTimes() {
            if (_.isNil(this.times)) {
                return
            }

            Object.keys(this.times).forEach(day => {
                let dayTimes = this.times[day]

                // There should only be one time interval set for an all day
                if (dayTimes.length !== 1) {
                    return
                }

                let interval = dayTimes[0]
                this.isAllDay[day] = this.getHourFromDateOrHour(interval.start) === 0
                    && this.getHourFromDateOrHour(interval.end) === 0
            })
        },
    },
    computed: {
        
    },
    watch: {
        timeChoice() {
            this.setTimes()
        },
        times() {
            this.formatTimesForServer()

            this.updateAllDayTimes()
        },

    },
}
</script>

<style scoped>
:deep(input[type="text"]) {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}


</style>